<template>
    <div class="slider-container" ref="sliderContainer" @mouseenter="stopSlider" @mouseleave="startSlider" v-bind="$attrs">
        <div class="slider-track" :style="trackStyle" v-bind="$attrs">
            <img :src="sliderImage" alt="Slider preview" @load="onImageLoad" />
        </div>

        <!-- Navigation buttons -->
        <button v-if="totalItems > 1" class="prev-btn" @click.stop.prevent="prevSlide" @mouseenter.stop="stopSlider"
            @mouseleave.stop="startSlider">&#10094;</button>
        <button v-if="totalItems > 1" class="next-btn" @click.stop.prevent="nextSlide" @mouseenter.stop="stopSlider"
            @mouseleave.stop="startSlider">&#10095;</button>
    </div>
</template>
  
<script>
export default {
    name: "ImageSlider",
    props: {
        sliderImage: {
            type: String,
            required: true
        },
        slideInterval: {
            type: Number,
            default: 2500
        },
        transitionTime: {
            type: Number,
            default: 800
        }
    },
    data() {
        return {
            currentIndex: 0,
            totalItems: 1,
            containerWidth: 0,
            naturalWidth: 0,
            naturalHeight: 0,
            timer: null,
            resizeTimeout: null
        };
    },
    computed: {
        slideWidth() {
            return this.containerWidth;
        },
        trackStyle() {
            return {
                transform: `translateX(-${this.currentIndex * this.slideWidth}px)`,
                transition: `transform ${this.transitionTime}ms ease-in-out`,
                display: "flex",
                width: this.totalItems * this.slideWidth + "px",
                height: "100%"
            };
        }
    },
    methods: {
        onImageLoad(event) {
            const img = event.target;

            this.naturalWidth = img.naturalWidth;
            this.naturalHeight = img.naturalHeight;

            // Calculate number of square slides
            this.totalItems = Math.max(
                1,
                Math.round(this.naturalWidth / this.naturalHeight)
            );

            this.recalculateSizes();
            this.$nextTick(() => {
                requestAnimationFrame(() => {
                    this.recalculateSizes();
                });
            });


            if (this.totalItems > 1) {
                this.startSlider();
            }
        },

        recalculateSizes() {
            if (!this.$refs.sliderContainer) return;

            this.containerWidth = this.$refs.sliderContainer.clientWidth;

            if (this.containerWidth == 0) {
                setTimeout(() => {
                    this.recalculateSizes();
                }, 100);
            }

            const img = this.$el.querySelector("img");
            if (img) {
                img.style.width = this.totalItems * this.containerWidth + "px";
                img.style.height = "100%";
            }

            // Prevent index overflow after resize
            if (this.currentIndex >= this.totalItems) {
                this.currentIndex = this.totalItems - 1;
            }
        },

        nextSlide() {
            this.currentIndex =
                (this.currentIndex + 1) % this.totalItems;
        },

        prevSlide() {
            this.currentIndex =
                (this.currentIndex - 1 + this.totalItems) % this.totalItems;
        },

        startSlider() {
            this.stopSlider();
            if (this.totalItems > 1) {
                this.timer = setInterval(
                    this.nextSlide,
                    this.slideInterval
                );
            }
        },

        stopSlider() {
            if (this.timer) {
                clearInterval(this.timer);
                this.timer = null;
            }
        },

        handleResize() {
            this.recalculateSizes();
        }
    },

    mounted() {
        window.addEventListener("resize", this.handleResize);
    },

    beforeDestroy() {
        this.stopSlider();
        window.removeEventListener("resize", this.handleResize);
    }
};
</script>
  
<style scoped>
.slider-container {
    height: 100%;
    width: 100%;
    overflow: hidden;
    position: relative;
}

/* Track */
.slider-track {
    display: flex;
    height: 100%;
}

/* Image styling */
.slider-track img {
    flex-shrink: 0;
    display: block;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

/* Navigation buttons */
.prev-btn,
.next-btn {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(0, 0, 0, 0.4);
    color: white;
    border: none;
    padding: 10px;
    cursor: pointer;
    font-size: 24px;
    z-index: 10;
    opacity: 0;
    transition: opacity 0.3s;
    user-select: none;
}

.prev-btn:hover,
.next-btn:hover {
    background-color: rgba(0, 0, 0, 0.6);
}

.slider-container:hover .prev-btn,
.slider-container:hover .next-btn {
    opacity: 1;
}

.prev-btn {
    left: 10px;
}

.next-btn {
    right: 10px;
}
</style>